import { useEffect } from 'react'
import dayjs from 'dayjs'

import { getAccountGivingActions } from '@percent/workplace-giving/api/account/givingActions/getAccountGivingActions/getAccountGivingActions'
import type { AccountGivingActionRow } from '@percent/workplace-giving/api/account/givingActions/getAccountGivingActions/getAccountGivingActions.types'
import { useAuth, useInfiniteQuery, useLogger } from '@percent/workplace-giving/common/hooks'
import { POSTHOG_FEATURE_FLAGS } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'
import { usePostHogFeatureFlag } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/usePostHogFeatureFlag'
import { getCountryCodeFromAuthState } from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'

export const UPCOMING_GIVING_ACTIONS_COUNT = 3

export type UseUpcomingGivingActions = Readonly<{
  givingActions: AccountGivingActionRow[]
  isPending: boolean
}>

export const useUpcomingGivingActions = (): UseUpcomingGivingActions => {
  const { state } = useAuth()
  const { logError } = useLogger()
  const givingActionsEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.WPG_GIVING_ACTIONS)

  const country = getCountryCodeFromAuthState(state)

  const { data, isPending, isError, error } = useInfiniteQuery(
    [
      'getAccountGivingActions',
      {
        date: dayjs().format('YYYY-MM-DD'),
        country,
        status: 'completable' as const,
        pageSize: UPCOMING_GIVING_ACTIONS_COUNT,
        direction: 'DESC' as const
      }
    ],
    getAccountGivingActions,
    { staleTime: 1000 * 20, enabled: givingActionsEnabled }
  )

  useEffect(() => {
    if (isError && error) {
      logError(error)
    }
  }, [isError, error, logError])

  return {
    givingActions: (data?.pages.flatMap(page => page.data) ?? []).slice(0, UPCOMING_GIVING_ACTIONS_COUNT),
    // A disabled query stays pending forever, so it must not block the section.
    isPending: givingActionsEnabled && isPending
  }
}
